import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
    setSearchColumns,
    setSearchSorting,
    selectSearchColumns,
    selectSearchSorting,
} from '../../store/slices/datagridSlice';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Tooltip } from '@mui/material';
import { colors } from '../../constants';
import { convertKeyToLabel } from '../../utilities/stringHelpers';

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const ColumnOrderSection = () => {
    const dispatch = useAppDispatch();
    const columns = useAppSelector(selectSearchColumns);
    const ordering = useAppSelector(selectSearchSorting);

    const tableColumns = useMemo(
        () =>
            columns.map((col) => ({
                headerName: col,
                field: col,
                width: 175,
                renderHeader: (params) => {
                    if (!params.colDef) {
                        return null;
                    }
                    const words = params.colDef.headerName.split('.');
                    return (
                        <Tooltip title={params.colDef.headerName} arrow>
                            <div
                                className="MuiDataGrid-columnHeaderTitle"
                                style={{
                                    minWidth: '162px',
                                }}
                            >
                                {words.map((w) => (
                                    <div key={w}>{convertKeyToLabel(w)}</div>
                                ))}
                            </div>
                        </Tooltip>
                    );
                },
            })),
        [columns]
    );

    const sortModel = useMemo(
        () =>
            ordering.map((sortCol) => {
                const [key] = Object.keys(sortCol);
                return { field: key, sort: sortCol[key] };
            }),
        [ordering]
    );

    const handleColumnOrderChange = (e) => {
        if (e?.targetIndex >= 0 && e?.oldIndex >= 0) {
            const updatedColumns = reorder(columns, e.oldIndex, e.targetIndex);
            dispatch(setSearchColumns(updatedColumns));
        }
    };

    const handleSortChange = (s) => {
        const updatedSorting = s.length ? structuredClone(ordering) : [];
        for (let sortField of s) {
            const { field, sort } = sortField;
            const index = updatedSorting.findIndex(
                (sortCol) => Object.keys(sortCol)[0] === field
            );
            if (index >= 0) {
                updatedSorting[index] = { [field]: sort };
            } else {
                updatedSorting.push({
                    [field]: sort,
                });
            }
        }
        dispatch(setSearchSorting(updatedSorting));
    };

    return (
        <div className="w-100 columnOrderContainer">
            <h3 className="fs-6" style={{ color: colors.skyfallMedium }}>
        Order
            </h3>
            <div className="w-100">
                <DataGridPro
                    rows={[]}
                    columns={tableColumns}
                    columnHeaderHeight="auto"
                    pagination={false}
                    disableColumnPinning
                    disableColumnSelector
                    disableColumnFilter
                    disableColumnResize
                    sortModel={sortModel}
                    onColumnOrderChange={handleColumnOrderChange}
                    onSortModelChange={handleSortChange}
                    sx={{
                        '& .MuiDataGrid-footerContainer': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-main': {
                            maxHeight: '52px',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: '700',
                            whiteSpace: 'normal',
                            lineHeight: 'normal',
                            color: colors.walnut,
                            overflow: 'visible',
                            textOverflow: 'clip',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            paddingTop: '4px',
                            paddingBottom: '4px',
                            whiteSpace: 'normal',
                            lineHeight: '1.2',
                            alignItems: 'start',
                            // Forced to use important since overriding inline styles
                            height: 'unset !important',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            // Forced to use important since overriding inline styles
                            maxHeight: '168px !important',
                        },
                    }}
                />
            </div>
        </div>
    );
};

export default ColumnOrderSection;
